import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Section, FadeIn, Button } from './ui';
import { SparklesIcon, ArrowRightIcon, ClockIcon, UsersIcon, TargetIcon, ZapIcon, CalendarIcon, WandIcon } from 'lucide-react';
export function AIDesigner() {
  const [activeChips, setActiveChips] = useState<Record<string, string>>({
    type: 'Retreat',
    location: 'Resort',
    size: '10-50',
    length: '3 Days',
    audience: 'Leadership',
    objective: 'Alignment',
    energy: 'Balanced'
  });
  // Add id to the root section for anchor routing
  const categories = [{
    id: 'type',
    label: 'Event Type',
    options: ['Meeting', 'Retreat', 'Training', 'Kickoff']
  }, {
    id: 'location',
    label: 'Location',
    options: ['Hotel', 'Resort', 'City', 'Nature']
  }, {
    id: 'size',
    label: 'Attendees',
    options: ['< 10', '10-50', '50-150', '150+']
  }, {
    id: 'length',
    label: 'Length',
    options: ['1 Day', '2 Days', '3 Days', 'Week']
  }, {
    id: 'audience',
    label: 'Audience',
    options: ['Leadership', 'Sales', 'Engineering', 'All Company']
  }, {
    id: 'objective',
    label: 'Objective',
    options: ['Alignment', 'Strategy', 'Bonding', 'Learning']
  }, {
    id: 'energy',
    label: 'Energy',
    options: ['High Energy', 'Balanced', 'Relaxed', 'Intense']
  }];
  const set = (id: string, opt: string) => setActiveChips(p => ({
    ...p,
    [id]: opt
  }));
  // Live preview agenda based on selections
  const day1 = [{
    t: '16:00',
    l: `Welcome circle · ${activeChips.audience}`
  }, {
    t: '17:30',
    l: activeChips.energy === 'Relaxed' ? 'Sunset cocktail hour' : 'Icebreaker activity'
  }, {
    t: '19:00',
    l: 'Welcome dinner'
  }];
  const day2 = [{
    t: '07:30',
    l: activeChips.energy === 'High Energy' ? 'Morning HIIT + cold plunge' : 'Sunrise yoga'
  }, {
    t: '09:00',
    l: `${activeChips.objective} module`
  }, {
    t: '14:00',
    l: 'Wellness break'
  }, {
    t: '16:30',
    l: 'Group discussion pods'
  }];
  return <Section id="ai-designer" className="bg-slate-50 relative overflow-hidden" data-id="element-119">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-120">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-brand-teal/[0.07] rounded-full blur-3xl" data-id="element-121" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.06] rounded-full blur-3xl" data-id="element-122" />
      </div>

      <div className="relative z-10 text-center max-w-3xl mx-auto mb-12" data-id="element-123">
        <FadeIn data-id="element-124">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-4 py-1 mb-5 shadow-sm" data-id="element-125">
            <span className="bg-brand-coral/10 text-brand-coral text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider flex items-center gap-1" data-id="element-126">
              <SparklesIcon className="w-3 h-3" data-id="element-127" /> Live Demo
            </span>
            <span className="text-sm font-medium text-slate-600" data-id="element-128">
              Tap any chip — your blueprint updates in real time
            </span>
          </div>
        </FadeIn>
        <FadeIn delay={0.05} data-id="element-129">
          <h2 className="text-3xl md:text-5xl font-bold text-brand-navy mb-4 leading-tight text-balance" data-id="element-130">
            Sketch your offsite in 30 seconds.
          </h2>
          <p className="text-lg text-slate-600" data-id="element-131">
            Pick a few options on the left. Watch your AI-designed agenda
            assemble on the right.
          </p>
        </FadeIn>
      </div>

      {/* Unified workspace */}
      <FadeIn delay={0.15} data-id="element-132">
        <div className="relative bg-white rounded-3xl shadow-xl shadow-slate-300/40 border border-slate-200 overflow-hidden max-w-6xl mx-auto" data-id="element-133">
          {/* Soft inner gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-brand-teal/[0.04] pointer-events-none" data-id="element-134" />

          {/* Top bar — workspace header */}
          <div className="relative z-10 flex items-center justify-between px-6 md:px-8 py-4 border-b border-slate-100 bg-slate-50/60 backdrop-blur" data-id="element-135">
            <div className="flex items-center gap-2" data-id="element-136">
              <div className="flex gap-1.5" data-id="element-137">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-200" data-id="element-138" />
                <span className="w-2.5 h-2.5 rounded-full bg-slate-200" data-id="element-139" />
                <span className="w-2.5 h-2.5 rounded-full bg-slate-200" data-id="element-140" />
              </div>
              <span className="ml-3 text-xs font-bold text-slate-400 uppercase tracking-[0.2em]" data-id="element-141">
                Offsite+ Designer
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs" data-id="element-142">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-143" />
              <span className="font-mono text-slate-500" data-id="element-144">live preview</span>
            </div>
          </div>

          <div className="relative z-10 grid lg:grid-cols-2" data-id="element-145">
            {/* LEFT: Form */}
            <div className="p-6 md:p-8 lg:border-r lg:border-dashed lg:border-slate-200" data-id="element-146">
              <div className="space-y-6" data-id="element-147">
                {categories.map(cat => <div key={cat.id} data-id="element-148">
                    <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.15em] mb-2.5" data-id="element-149">
                      {cat.label}
                    </h4>
                    <div className="flex flex-wrap gap-1.5" data-id="element-150">
                      {cat.options.map(option => {
                    const active = activeChips[cat.id] === option;
                    return <motion.button key={option} whileTap={{
                      scale: 0.96
                    }} onClick={() => set(cat.id, option)} className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${active ? 'bg-brand-navy text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-151">
                            {option}
                          </motion.button>;
                  })}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Connector arrow on lg */}
            <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white shadow-lg border border-slate-200 items-center justify-center" data-id="element-152">
              <motion.div animate={{
              x: [0, 4, 0]
            }} transition={{
              duration: 1.6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-153">
                <ArrowRightIcon className="w-5 h-5 text-brand-teal" data-id="element-154" />
              </motion.div>
            </div>

            {/* RIGHT: Live preview */}
            <div className="p-6 md:p-8 bg-gradient-to-br from-slate-50/80 to-white" data-id="element-155">
              <div className="flex items-center gap-2 mb-5" data-id="element-156">
                <div className="w-8 h-8 rounded-lg bg-brand-coral/10 text-brand-coral flex items-center justify-center" data-id="element-157">
                  <WandIcon className="w-4 h-4" data-id="element-158" />
                </div>
                <div data-id="element-159">
                  <div className="text-[10px] font-bold text-brand-coral uppercase tracking-[0.2em]" data-id="element-160">
                    AI-generated · live
                  </div>
                  <div className="text-base font-bold text-brand-navy leading-tight" data-id="element-161">
                    Your Offsite Blueprint
                  </div>
                </div>
              </div>

              {/* Summary chips */}
              <motion.div key={Object.values(activeChips).join('-')} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} className="grid grid-cols-2 gap-2 mb-5" data-id="element-162">
                <SummaryTile icon={<CalendarIcon className="w-3.5 h-3.5" data-id="element-164" />} label={`${activeChips.length} ${activeChips.type}`} sub={activeChips.location} data-id="element-163" />
                <SummaryTile icon={<UsersIcon className="w-3.5 h-3.5" data-id="element-166" />} label={activeChips.size} sub={activeChips.audience} data-id="element-165" />
                <SummaryTile icon={<TargetIcon className="w-3.5 h-3.5" data-id="element-168" />} label={activeChips.objective} sub="Primary objective" data-id="element-167" />
                <SummaryTile icon={<ZapIcon className="w-3.5 h-3.5" data-id="element-170" />} label={activeChips.energy} sub="Pacing" data-id="element-169" />
              </motion.div>

              {/* Agenda preview card */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-171">
                <div className="flex items-center justify-between mb-3" data-id="element-172">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.15em]" data-id="element-173">
                    Sample agenda
                  </span>
                  <span className="text-[10px] font-bold text-brand-teal flex items-center gap-1" data-id="element-174">
                    <ClockIcon className="w-3 h-3" data-id="element-175" /> regenerates instantly
                  </span>
                </div>

                <DayBlock label="Day 1 · Arrival" items={day1} accent="teal" data-id="element-176" />
                <div className="my-3 border-t border-dashed border-slate-200" data-id="element-177" />
                <DayBlock label="Day 2 · Core" items={day2} accent="coral" data-id="element-178" />
              </div>

              {/* Includes pills */}
              <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-179">
                {['Intake form', 'Group matching', 'Reflection prompts', 'Wellness layer', 'Post-event report'].map((p, i) => <motion.span key={p} initial={{
                opacity: 0,
                scale: 0.9
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: i * 0.06
              }} viewport={{
                once: true
              }} className="bg-slate-100 text-slate-700 text-[10px] font-bold px-2 py-1 rounded-md" data-id="element-180">
                    + {p}
                  </motion.span>)}
              </div>
            </div>
          </div>

          {/* Bottom CTA bar */}
          <div className="relative z-10 border-t border-slate-100 bg-slate-50/60 backdrop-blur px-6 md:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-181">
            <div className="text-center sm:text-left" data-id="element-182">
              <div className="text-sm font-bold text-brand-navy" data-id="element-183">
                Like what you see? Generate the full plan.
              </div>
              <div className="text-xs text-slate-500" data-id="element-184">
                Free, no credit card · ~15 seconds · 12 pages of detail
              </div>
            </div>
            <Button variant="accent" size="lg" href="/designer" className="group whitespace-nowrap" data-id="element-185">
              <SparklesIcon className="w-4 h-4 mr-2" data-id="element-186" />
              Generate My Free Offsite Plan
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-187" />
            </Button>
          </div>
        </div>
      </FadeIn>
    </Section>;
}
function SummaryTile({
  icon,
  label,
  sub
}: {
  icon: React.ReactNode;
  label: string;
  sub: string;
}) {
  return <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2.5" data-id="element-188">
      <div className="w-7 h-7 rounded-md bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0" data-id="element-189">
        {icon}
      </div>
      <div className="min-w-0" data-id="element-190">
        <div className="text-xs font-bold text-brand-navy truncate" data-id="element-191">
          {label}
        </div>
        <div className="text-[10px] text-slate-500 truncate" data-id="element-192">{sub}</div>
      </div>
    </div>;
}
function DayBlock({
  label,
  items,
  accent
}: {
  label: string;
  items: {
    t: string;
    l: string;
  }[];
  accent: 'teal' | 'coral';
}) {
  const dot = accent === 'teal' ? 'bg-brand-teal' : 'bg-brand-coral';
  return <div data-id="element-193">
      <div className="flex items-center gap-2 mb-2" data-id="element-194">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} data-id="element-195" />
        <span className="text-xs font-bold text-brand-navy" data-id="element-196">{label}</span>
      </div>
      <div className="space-y-1.5 pl-3.5" data-id="element-197">
        {items.map((it, i) => <motion.div key={`${label}-${it.l}`} initial={{
        opacity: 0,
        x: -4
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: i * 0.05
      }} className="flex gap-3 text-xs" data-id="element-198">
            <span className="font-mono text-slate-400 w-10 shrink-0" data-id="element-199">
              {it.t}
            </span>
            <span className="text-slate-700" data-id="element-200">{it.l}</span>
          </motion.div>)}
      </div>
    </div>;
}